import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Icon } from '../ui';
import type { Editor } from '@tiptap/react';

export interface SlashCommandItem {
  id: string;
  title: string;
  description: string;
  icon: string;
  keywords: string[];
  run: (editor: Editor) => void;
}

export interface SlashCommandListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

interface SlashCommandListProps {
  items: SlashCommandItem[];
  command: (item: SlashCommandItem) => void;
  editor: Editor;
}

export const SlashCommandList = forwardRef<SlashCommandListRef, SlashCommandListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => setSelectedIndex(0), [props.items]);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) props.command(item);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((i) => (i + props.items.length - 1) % props.items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((i) => (i + 1) % props.items.length);
        return true;
      }
      if (event.key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (props.items.length === 0) {
    return (
      <div className="tt-slash-list">
        <div className="tt-slash-empty">
          <Icon name="search_off" size={16} />
          <span>לא נמצאו פעולות</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tt-slash-list">
      <div className="tt-slash-header">פעולות מהירות</div>
      {props.items.map((item, index) => (
        <button
          key={item.id}
          type="button"
          className={[
            'tt-slash-item',
            selectedIndex === index ? 'tt-slash-item-active' : '',
          ].join(' ')}
          onClick={() => selectItem(index)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div className="tt-slash-icon">
            <Icon name={item.icon} size={18} />
          </div>
          <div className="tt-slash-text">
            <div className="tt-slash-title">{item.title}</div>
            <div className="tt-slash-desc">{item.description}</div>
          </div>
        </button>
      ))}
    </div>
  );
});

SlashCommandList.displayName = 'SlashCommandList';
